import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import { Box, TableHead } from '@mui/material';
import {
  Datagrid, Identifier, useTranslate,
} from 'react-admin';
import { useMemo } from 'react';
import { securityItemsResource } from '../util';
import { getExpiryStatus } from '../../../../../utils/certificationsAndItemsExpiry';
import { calculateLargestReminderPeriodInDays } from '../../../../../utils/timeGranularityUtils';
import { DataGridListItem, InventoryItemListRecord } from './DataGridListItem';

type InventoryItemsDatagridProps = {
  items: SecurityInventoryItem[];
  isLoading: boolean;
  categories: SecurityInventoryCategory[];
  visible: boolean;
  selectable: boolean;
  hoveredItemId: Identifier | null;
  selectedIds: Identifier[];
  onHover: (itemId: Identifier | null) => void;
  onSelectionChange: (selectedIds: Identifier[]) => void;
  onEdit: (item: SecurityInventoryItem) => void;
};

export const InventoryItemsDatagrid = (props: InventoryItemsDatagridProps) => {

  const translate = useTranslate();

  const itemsWithExpiryStatus = useMemo<InventoryItemListRecord[]>(() => {

    const reminderDaysByCategoryId = Object.fromEntries(
      props.categories.map((category) => [
        category._id,
        calculateLargestReminderPeriodInDays(category.reminderPeriods ?? []),
      ]),
    );

    return props.items.map((item) => ({
      ...item,
      expiryStatus: getExpiryStatus(
        item.expiryDate,
        reminderDaysByCategoryId[item.category._id] ?? 0,
      ),
    }));

  }, [props.items, props.categories]);

  return (
    <Datagrid
      resource={securityItemsResource}
      data={itemsWithExpiryStatus}
      total={itemsWithExpiryStatus.length}
      isLoading={props.isLoading}
      empty={
        <Box sx={{
          px: 2, py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 14,
        }}>
          {translate('resources.security-inventory-items.text.no_results')}
        </Box>
      }
      bulkActionButtons={false}
      rowClick={false}
      hover={false}
      header={<TableHead />}
      rowSx={(record: InventoryItemListRecord, index: number) => {

        if (!props.visible) {

          return {
            backgroundColor: index % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
            color: 'text.disabled',
            '& .button-sidebar-grid-row': {
              filter: 'grayscale(1)',
              opacity: 0.65,
            },
          };

        }
        if (props.selectedIds.includes(record._id)) {

          return {
            backgroundColor: '#9FD2B6',
          };

        }
        if (props.hoveredItemId === record._id) {

          return {
            backgroundColor: index % 2 === 0 ? '#E8F4EC' : '#D8EDE4',
            outline: '2px solid #2E7D57',
            outlineOffset: -2,
          };

        }
        return {
          backgroundColor: index % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
        };

      }}
      sx={{
        '& .RaDatagrid-expandIconCell': {
          display: 'none',
        },
      }}
    >
      <DataGridListItem
        categories={props.categories}
        mouseHoverId={props.hoveredItemId}
        onHover={props.onHover}
        selectable={props.selectable}
        selectedIds={props.selectedIds}
        onSelectionChange={props.onSelectionChange}
        onEdit={props.onEdit}
      />
    </Datagrid>
  );

};
